import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { records, search, rag } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Upload, Globe, Archive, Filter, RefreshCw, Eye, CheckCircle, Sparkles, Trash2, Loader, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

// Demo-PREP convenience only: one click clears the R2 file + DB record + embeddings
// together so we never hand-clean the bucket. TURN OFF before any big/public demo —
// set to false and the delete button disappears.
const ENABLE_DELETE = true;

const STATUS_STYLES = {
  published: 'bg-green-50 text-green-700 border-green-200',
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  archived: 'bg-amber-50 text-amber-700 border-amber-200',
};

// Why a record can show no text: OCR for scans/images runs in a background lane,
// so an empty ocr_text means "pending", "nothing found" or "it failed" — never
// just nothing. Showing the reason is the difference between a working feature
// and one that looks broken.
function OcrStatusLine({ status }) {
  if (status === 'pending' || status === 'running') {
    return (
      <div className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
        <Loader size={10} className="animate-spin" /> Reading document...
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
        <AlertCircle size={10} /> Text extraction failed
      </div>
    );
  }
  if (status === 'empty') {
    return (
      <div className="text-xs text-amber-500 mt-0.5 flex items-center gap-1">
        <AlertCircle size={10} /> No text found
      </div>
    );
  }
  return null;
}

export default function RecordsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ records: [], total: 0 });
  const [facets, setFacets] = useState({ types: [], departments: [] });
  const [filters, setFilters] = useState({ type: '', department: '', status: '', page: 1 });
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [ingesting, setIngesting] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (r) => {
    if (!window.confirm(`Permanently delete "${r.title}"?\n\nThis removes the file from storage (R2) AND the record from the database, and cannot be undone.`)) return;
    setDeleting(r.id);
    try {
      const { data } = await records.delete(r.id);
      if (data?.storage_deleted === false) {
        toast(`Record deleted, but the storage file may remain: ${data.storage_warning || 'check R2'}`, { icon: '⚠️' });
      } else {
        toast.success('File + record permanently deleted');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleIngest = async (id) => {
    setIngesting(id);
    try {
      const { data } = await rag.ingest(id);
      toast.success(`Indexed ${data.embedded} chunks`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Indexing failed'); }
    finally { setIngesting(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([records.list(filters), search.facets()]);
      setData(r.data);
      setFacets(f.data);
    } catch (e) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const handlePublish = async (id, currentStatus) => {
    setPublishing(id);
    try {
      if (currentStatus === 'published') {
        await records.unpublish(id);
        toast.success('Record unpublished');
      } else {
        await records.publish(id);
        toast.success('Record published to public portal');
      }
      load();
    } catch (e) {
      toast.error('Action failed');
    } finally {
      setPublishing(null);
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1e6) return `${(bytes/1e3).toFixed(0)} KB`;
    return `${(bytes/1e6).toFixed(1)} MB`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Records</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data.total.toLocaleString()} total records</p>
        </div>
        <Link to="/upload" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
          <Upload size={15} /> Upload
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 flex flex-wrap gap-3 items-center">
        <Filter size={14} className="text-gray-400" />
        <select value={filters.type} onChange={e => setFilter('type', e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-400 min-w-[140px]">
          <option value="">All types</option>
          {facets.types?.map(t => <option key={t.document_type} value={t.document_type}>{t.document_type} ({t.count})</option>)}
        </select>
        <select value={filters.department} onChange={e => setFilter('department', e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-400 min-w-[140px]">
          <option value="">All departments</option>
          {facets.departments?.map(d => <option key={d.department} value={d.department}>{d.department} ({d.count})</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-blue-400">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={load} className="ml-auto text-gray-400 hover:text-gray-600 transition-colors">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading && data.records.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">Loading records...</div>
        ) : data.records.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-sm text-gray-400 mb-3">No records found</div>
            <Link to="/upload" className="text-sm text-blue-600 hover:text-blue-700">Upload your first record →</Link>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50 bg-gray-50/50">
                  {['Title','Type','Department','Size','Status','Date','Actions'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.records.map(r => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40 transition-colors">
                    <td className="px-5 py-3.5 max-w-xs">
                      <Link to={`/records/${r.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate block transition-colors">{r.title}</Link>
                      {r.ocr_text
                        ? <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><CheckCircle size={10} className="text-teal-500" /> OCR indexed</div>
                        : <OcrStatusLine status={r.metadata?.ocr_status} />}
                      {r.metadata?.rag_indexed && <div className="text-xs text-purple-500 mt-0.5 flex items-center gap-1"><Sparkles size={10} /> RAG indexed</div>}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-500 whitespace-nowrap">{r.document_type}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-500">{r.department || '—'}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">{fmtSize(r.file_size)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                      {r.record_date ? format(new Date(r.record_date), 'MMM d, yyyy') : format(new Date(r.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <Link to={`/records/${r.id}`} className="text-gray-400 hover:text-blue-600 transition-colors" title="View details">
                          <Eye size={15} />
                        </Link>
                        {(user?.role === 'admin' || user?.role === 'staff') && r.ocr_text && (
                          <button onClick={() => handleIngest(r.id)} disabled={ingesting === r.id} title="Index for RAG search"
                            className={`transition-colors ${r.metadata?.rag_indexed ? 'text-purple-400 hover:text-purple-600' : 'text-gray-300 hover:text-purple-500'}`}>
                            <Sparkles size={15} className={ingesting === r.id ? 'animate-pulse' : ''} />
                          </button>
                        )}
                        {(user?.role === 'admin' || user?.role === 'staff') && (
                          <button onClick={() => handlePublish(r.id, r.status)} disabled={publishing === r.id}
                            title={r.status === 'published' ? 'Unpublish' : 'Publish to public portal'}
                            className={`transition-colors ${r.status === 'published' ? 'text-green-500 hover:text-amber-500' : 'text-gray-300 hover:text-green-500'}`}>
                            <Globe size={15} className={publishing === r.id ? 'animate-pulse' : ''} />
                          </button>
                        )}
                        {ENABLE_DELETE && user?.role === 'admin' && (
                          <button onClick={() => handleDelete(r)} disabled={deleting === r.id}
                            title="Permanently delete (file + record)"
                            className="text-gray-300 hover:text-red-600 transition-colors">
                            <Trash2 size={15} className={deleting === r.id ? 'animate-pulse' : ''} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination */}
            {data.total > 20 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-50">
                <div className="text-xs text-gray-400">
                  Showing {(filters.page - 1) * 20 + 1}–{Math.min(filters.page * 20, data.total)} of {data.total.toLocaleString()}
                </div>
                <div className="flex gap-2">
                  <button disabled={filters.page === 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                    className="px-3 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">← Prev</button>
                  <button disabled={filters.page * 20 >= data.total} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                    className="px-3 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
